import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import type {
  CreateDeliveryProviderInput,
  UpdateDeliveryProviderInput,
  CreateDeliveryTransactionInput,
  DeliveryTransactionFilters,
  DeliveryLogFilters,
} from '@erp/data-access/interfaces';

const ROOT = ['delivery-providers'] as const;
const detailKey = (id: string) => [...ROOT, 'detail', id] as const;
const txKey = (filters: DeliveryTransactionFilters) =>
  [...ROOT, 'tx', filters.providerId, filters.status ?? 'all', filters.kind ?? 'all', filters.shipmentId ?? 'all'] as const;
const logKey = (filters?: DeliveryLogFilters) =>
  [...ROOT, 'logs', filters?.providerId ?? 'all', filters?.shipmentId ?? 'all'] as const;
const pickupsKey = (providerId: string) => [...ROOT, 'pickups', providerId] as const;
const summaryKey = (providerId: string) => [...ROOT, 'summary', providerId] as const;

export function useDeliveryProviders() {
  const ds = useDataService();
  return useQuery({
    queryKey: ROOT,
    queryFn: () => ds.deliveryProviders.list(),
  });
}

export function useDefaultDeliveryProvider() {
  const ds = useDataService();
  return useQuery({
    queryKey: [...ROOT, 'default'] as const,
    queryFn: () => ds.deliveryProviders.getDefault(),
  });
}

export function useDeliveryProvider(id: string | undefined) {
  const ds = useDataService();
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => (id ? ds.deliveryProviders.getById(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useDeliveryProviderByCode(code: string | undefined) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...ROOT, 'by-code', code ?? ''] as const,
    queryFn: () => (code ? ds.deliveryProviders.getByCode(code) : Promise.resolve(null)),
    enabled: !!code,
  });
}

export function useCreateDeliveryProvider() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDeliveryProviderInput) => ds.deliveryProviders.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useUpdateDeliveryProvider(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDeliveryProviderInput) => ds.deliveryProviders.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useDeleteDeliveryProvider() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.deliveryProviders.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useSetDefaultDeliveryProvider() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ds.deliveryProviders.setDefault(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

// ---- Wallet ----

export function useDeliveryTransactions(filters: DeliveryTransactionFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: txKey(filters),
    queryFn: () => ds.deliveryProviders.listTransactions(filters),
    enabled: !!filters.providerId,
  });
}

export function useCreateDeliveryTransaction() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDeliveryTransactionInput) => ds.deliveryProviders.createTransaction(input),
    onSuccess: (tx) => {
      qc.invalidateQueries({ queryKey: [...ROOT, 'tx', tx.providerId] });
      qc.invalidateQueries({ queryKey: summaryKey(tx.providerId) });
      qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useRefreshWalletSummary(providerId: string | undefined) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!providerId) throw new Error('No provider selected');
      return ds.deliveryProviders.refreshWalletSummary(providerId);
    },
    onSuccess: () => {
      if (!providerId) return;
      qc.invalidateQueries({ queryKey: summaryKey(providerId) });
      qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useMarkTransactionsPaidOut() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => ds.deliveryProviders.markTransactionsPaidOut(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOT }),
  });
}

// ---- Pickups & logs ----

export function useDeliveryPickups(providerId: string | undefined) {
  const ds = useDataService();
  return useQuery({
    queryKey: pickupsKey(providerId ?? ''),
    queryFn: () => (providerId ? ds.deliveryProviders.listPickups(providerId) : Promise.resolve([])),
    enabled: !!providerId,
  });
}

export function useDeliveryLogs(filters?: DeliveryLogFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: logKey(filters),
    queryFn: () => ds.deliveryProviders.listLogs(filters),
  });
}

// ---- Reference data ----

export function useDeliveryCities(providerId: string | undefined) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...ROOT, 'cities', providerId ?? ''] as const,
    queryFn: () => (providerId ? ds.deliveryProviders.listCities(providerId) : Promise.resolve([])),
    enabled: !!providerId,
  });
}

export function useDeliveryDistricts(cityId: string | undefined) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...ROOT, 'districts', cityId ?? ''] as const,
    queryFn: () => (cityId ? ds.deliveryProviders.listDistricts(cityId) : Promise.resolve([])),
    enabled: !!cityId,
  });
}

